<template>
  <Transition name="modal-fade">
    <div class="custom-modal-overlay" v-if="visible" @click.self="handleClose">
      <div class="custom-modal-container">
        <!-- Close button -->
        <button class="close-btn" @click="handleClose" title="关闭">
          <i class="fa-solid fa-xmark"></i>
        </button>

        <!-- Loading overlay -->
        <div class="modal-loading-overlay" v-if="loading">
          <div class="loading-spinner"></div>
        </div>

        <div class="modal-inner-scroll">
          <!-- Header Section -->
          <div class="shop-header">
            <div class="header-content">
              <h1 class="shop-title">个性化装扮中心</h1>
              <p class="shop-subtitle">使用每日签到积累的积分，获取尊贵身份标识与炫彩聊天特权</p>
            </div>
            <div class="points-badge">
              <span class="points-label">我的可用积分</span>
              <span class="points-value">
                {{ userPoints }} <span class="points-unit">点</span>
              </span>
            </div>
          </div>

          <!-- 3D Preview Section -->
          <div class="preview-panel">
            <h2 class="panel-title">✨ 专属特效实时预览 ✨</h2>
            
            <div class="preview-card-container">
              <!-- Live Avatar & Name Preview -->
              <div 
                class="avatar-preview-box-wrapper"
                @mousemove="handleCardTilt"
                @mouseleave="resetCardTilt"
                :style="tiltStyle"
              >
                <div class="avatar-preview-box">
                  <ThemeDecorator :skin-id="previewSkin.id" size="lg">
                    <template #avatar>
                      <img :src="userAvatar" @error="onAvatarError" />
                    </template>
                  </ThemeDecorator>
                  
                  <div class="name-box">
                    <ThemeDecorator :skin-id="previewSkin.id">
                      <template #nickname>{{ userNickname }}</template>
                    </ThemeDecorator>
                    <span class="username-preview">@{{ userInfo.username || 'Henvei' }}</span>
                  </div>
                </div>
              </div>

              <!-- Message Bubble Preview -->
              <div class="message-preview-box">
                <ThemeDecorator :skin-id="previewSkin.id">
                  <div class="chat-bubble chat-bubble-custom" :class="previewSkin.id">
                    <span class="bubble-text">
                      这是我在云语匀雨的专属皮肤！好看吗？🎉
                    </span>
                  </div>
                </ThemeDecorator>
              </div>
            </div>
          </div>

          <!-- Skins Grid -->
          <div class="skins-grid">
            <div 
              v-for="skin in skinsList" 
              :key="skin.id"
              class="skin-card"
              :class="{ 
                'equipped': activeSkin === skin.id,
                'previewing': previewSkin.id === skin.id
              }"
              @click="previewSkin = skin"
            >
              <!-- Equipped / Preview indicators -->
              <span v-if="activeSkin === skin.id" class="status-indicator equipped-badge">已装备</span>
              <span v-else-if="previewSkin.id === skin.id" class="status-indicator preview-badge">预览中</span>

              <div class="skin-card-header">
                <div class="frame-demo-box">
                  <ThemeDecorator :skin-id="skin.id" size="sm">
                    <template #avatar>
                      <img :src="userAvatar" @error="onAvatarError" />
                    </template>
                  </ThemeDecorator>
                </div>
                <div class="skin-info">
                  <h3 class="skin-name" :class="skin.id">{{ skin.name }}</h3>
                  <span v-if="unlockedSkins.includes(skin.id)" class="status-tag unlocked">已拥有</span>
                  <span v-else class="status-tag locked">未解锁</span>
                </div>
              </div>

              <div class="skin-card-body">
                <p class="skin-desc">{{ skin.desc }}</p>
              </div>

              <div class="skin-card-footer">
                <div class="skin-price">
                  <span class="price-num">{{ skin.price }}</span>
                  <span class="price-unit">积分</span>
                </div>

                <div class="skin-card-actions">
                  <!-- Equip / Equited -->
                  <button 
                    v-if="unlockedSkins.includes(skin.id)"
                    class="action-btn"
                    :class="activeSkin === skin.id ? 'active' : 'equip'"
                    :disabled="activeSkin === skin.id"
                    @click.stop="equipSkin(skin.id)"
                  >
                    <i class="fa-solid fa-shirt"></i>
                    {{ activeSkin === skin.id ? '正在使用' : '立即装备' }}
                  </button>
                  
                  <!-- Unlock -->
                  <button 
                    v-else
                    class="action-btn buy"
                    :disabled="userPoints < skin.price"
                    @click.stop="unlockSkin(skin.id)"
                  >
                    <i class="fa-solid fa-cart-shopping"></i>
                    {{ userPoints < skin.price ? '积分不足' : '立即兑换' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import serverUrl, { avatarUrl } from '@/assets/BaseServerUrl.js'
import axios from 'axios'
import { message, messageBox } from '@/utils/ui-service'
import eventBus from '@/utils/eventBus'
import ThemeDecorator from './ThemeDecorator.vue'
import skinVisualConfigCache from '@/utils/skinVisualConfigCache'

export default {
  name: 'StoreModal',
  components: {
    ThemeDecorator
  },
  data() {
    return {
      visible: false,
      loading: false,
      userPoints: 0,
      unlockedSkins: ['skin-default'],
      activeSkin: 'skin-default',
      skinsList: [
        { id: 'skin-default', name: '经典原版', price: 0, desc: '经典简约的云语匀雨专属默认头像框与字色，落落大方。' },
        { id: 'skin-aurora', name: '极光之境', price: 50, desc: '如梦似幻的北极光环绕着你，带来翠绿与蔚蓝交织的清新昵称与专属极光徽章。' },
        { id: 'skin-cyber', name: '赛博霓虹', price: 100, desc: '赛博朋克风电子霓虹灯效果，渐变粉紫昵称与独特的霓虹徽章，彰显前卫个性。' },
        { id: 'skin-sakura', name: '樱花浪漫', price: 150, desc: '落樱飞舞的浪漫粉红边框，配以柔美和风粉色昵称与专属樱花徽章。' },
        { id: 'skin-starry', name: '璀璨星空', price: 200, desc: '深邃夜空的蔚蓝光晕，金黄色流星尘埃的头像框与极富星云质感的璀璨徽章。' },
        { id: 'skin-abyss', name: '虚空深渊', price: 300, desc: '幽邃莫测的暗黑深渊，散发致命诱惑的紫色粒子流动头像框与神秘莫测的深渊徽章。' },
        { id: 'skin-gold', name: '黄金流沙', price: 220, desc: '璀璨黄金如流沙般倾泻而下，闪烁的金粒环绕头像框，搭配尊贵金色昵称与黄金徽章。' },
        { id: 'skin-phoenix', name: '涅槃烈焰', price: 280, desc: '炽烈火焰升腾不息，火星由金渐红的烈焰粒子流动头像框，配以烈焰徽章彰显浴火重生之气。' },
        { id: 'skin-ocean', name: '深海蔚蓝', price: 320, desc: '幽蓝深海气泡缓缓上升，波光粼粼的蔚蓝渐变头像框与深海徽章，尽显沉静神秘的海洋之力。' },
        { id: 'skin-thunder', name: '雷霆万钧', price: 260, desc: '电光石火间雷霆乍现，蓝紫色闪电分叉闪烁头像框，配以雷霆徽章，势若破竹威震四方。' },
        { id: 'skin-snow', name: '寒雪纷飞', price: 200, desc: '六角雪花凌空飘落，缓缓旋转呼吸起伏，冰蓝渐变头像框与寒雪徽章，尽显凛冬静谧之美。' },
        { id: 'skin-ink', name: '水墨丹青', price: 280, desc: '墨滴晕染徐徐扩散，淡墨游丝若隐若现，墨色渐变头像框与水墨徽章，尽显东方写意意境。' },
        { id: 'skin-firework', name: '璀璨烟花', price: 350, desc: '烟花绽放四散纷飞，重力阻力拖尾流转，多彩渐变头像框与烟花徽章，喜庆璀璨如夜空盛宴。' }
      ],
      previewSkin: { id: 'skin-default' },
      userInfo: {},
      tiltStyle: {}
    }
  },
  computed: {
    userAvatar() {
      const DEFAULT_AVATAR = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiM2NjdlZWEiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxNiIgcj0iNiIgZmlsbD0id2hpdGUiLz4KPHBhdGggZD0iTTggMzJjMC02LjYyNyA1LjM3My0xMiAxMi0xMnMxMiA1LjM3MyAxMiAxMiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+'
      const av = this.userInfo?.avatar
      if (av && av !== 'null' && av !== 'undefined' && av.trim() !== '') {
        if (av.startsWith('http')) {
          return av
        }
        return `${avatarUrl}/avatar/${av}`
      }
      return DEFAULT_AVATAR;
    },
    userNickname() {
      return this.userInfo.nickname || this.userInfo.username || '匿名用户'
    }
  },
  created() {
    eventBus.on('open-store-modal', () => {
      this.visible = true;
      this.initUser();
      this.fetchUserSkins();
    });
    eventBus.on('close-store-modal', () => {
      this.visible = false;
    });
  },
  beforeUnmount() {
    eventBus.off('open-store-modal');
    eventBus.off('close-store-modal');
  },
  methods: {
    handleClose() {
      this.visible = false;
      this.resetCardTilt();
    },
    initUser() {
      try {
        const info = JSON.parse(localStorage.getItem('userInfo') || '{}')
        this.userInfo = info
      } catch (e) {
        this.userInfo = {}
      }
    },
    async fetchUserSkins() {
      this.loading = true
      try {
        const response = await axios.get(`${serverUrl}/api/shop/skins`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        if (response.data.success) {
          this.userPoints = response.data.points
          this.unlockedSkins = response.data.unlockedSkins || ['skin-default']
          this.activeSkin = response.data.activeSkin || 'skin-default'
          
          if (response.data.skins) {
            this.skinsList = response.data.skins;
            // 同步皮肤 visualConfig 到全局缓存
            skinVisualConfigCache.setConfigs(response.data.skins);
          }

          // Initial preview set to active skin
          const current = this.skinsList.find(s => s.id === this.activeSkin)
          if (current) {
            this.previewSkin = current
          }
        }
      } catch (err) {
        message.error('获取个性化配置失败: ' + (err.response?.data?.message || err.message))
      } finally {
        this.loading = false
      }
    },
        unlockSkin(skinId) {
      const skin = this.skinsList.find(s => s.id === skinId)
      if (!skin) return
      
      if (this.userPoints < skin.price) {
        message.error('积分不足，快去签到获取积分吧！')
        return
      }

      messageBox.confirm(
        `您确定要使用 ${skin.price} 积分兑换装扮【${skin.name}】吗？`,
        '确认兑换',
        {
          type: 'warning'
        }
      ).then(async () => {
        this.loading = true
        try {
          const response = await axios.post(`${serverUrl}/api/shop/skins/unlock`, { skinId }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          })
          if (response.data.success) {
            message.success(`成功兑换皮肤 ${skin.name}！`)
            this.userPoints = response.data.points
            this.unlockedSkins = response.data.unlockedSkins

            // Auto equip after unlock
            await this.equipSkin(skinId)
          }
        } catch (err) {
          message.error('解锁失败: ' + (err.response?.data?.message || err.message))
        } finally {
          this.loading = false
        }
      }).catch(() => {
        // 用户取消兑换，无需处理
      })
    },
    async equipSkin(skinId) {
      this.loading = true
      try {
        const response = await axios.post(`${serverUrl}/api/shop/skins/equip`, { skinId }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        if (response.data.success) {
          message.success('装备成功！')
          this.activeSkin = response.data.activeSkin
          
          // Emit event to update all frontend active skins immediately
          eventBus.emit('skin-changed', this.activeSkin)

          try {
            const info = JSON.parse(localStorage.getItem('userInfo') || '{}')
            info.activeSkin = this.activeSkin
            localStorage.setItem('userInfo', JSON.stringify(info))
          } catch (_) {
            // ignore
          }
        }
      } catch (err) {
        message.error('装备失败: ' + (err.response?.data?.message || err.message))
      } finally {
        this.loading = false
      }
    },
    onAvatarError(e) {
      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiM2NjdlZWEiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxNiIgcj0iNiIgZmlsbD0id2hpdGUiLz4KPHBhdGggZD0iTTggMzJjMC02LjYyNyA1LjM3My0xMiAxMi0xMnMxMiA1LjM3MyAxMiAxMiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+'
    },
    handleCardTilt(e) {
      const card = e.currentTarget;
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      
      // Calculate rotation angles (max 12deg)
      const rotateX = -(y / (rect.height / 2)) * 12;
      const rotateY = (x / (rect.width / 2)) * 12;
      
      this.tiltStyle = {
        transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.03, 1.03, 1.03)`,
        transition: 'transform 0.08s ease-out'
      };
    },
    resetCardTilt() {
      this.tiltStyle = {
        transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)',
        transition: 'transform 0.3s ease'
      };
    }
  }
}
</script>

<style scoped>
/* Full Screen Glass overlay */
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-modal-container {
  width: 90%;
  max-width: 1200px;
  max-height: 85vh;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 28px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.12);
  position: relative;
  overflow: hidden;
  padding: 40px;
  box-sizing: border-box;
}

.modal-inner-scroll {
  max-height: 75vh;
  overflow-y: auto;
  padding-right: 8px;
  box-sizing: border-box;
}

/* Custom Scrollbar for Modal Inner Scroll */
.modal-inner-scroll::-webkit-scrollbar {
  width: 6px;
}
.modal-inner-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.modal-inner-scroll::-webkit-scrollbar-thumb {
  background: rgba(120, 120, 120, 0.2);
  border-radius: 3px;
}
.modal-inner-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(120, 120, 120, 0.4);
}

/* Close Button */
.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.04);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: #1d1d1f;
  transition: all 0.2s;
  z-index: 10;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.08);
  transform: rotate(90deg);
}

/* Loading Overlay */
.modal-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 113, 227, 0.1);
  border-top-color: #0071e3;
  border-radius: 50%;
  animation: spinner-rot 0.8s linear infinite;
}

@keyframes spinner-rot {
  to { transform: rotate(360deg); }
}

/* Header Component */
.shop-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  background: rgba(255, 255, 255, 0.4);
  padding: 20px 28px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.shop-title {
  font-size: 2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #1d1d1f 0%, #0071e3 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0 0 6px 0;
}

.shop-subtitle {
  font-size: 0.9rem;
  color: #86868b;
  margin: 0;
}

.points-badge {
  background: rgba(0, 113, 227, 0.06);
  border: 1px solid rgba(0, 113, 227, 0.12);
  padding: 10px 20px;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.points-label {
  font-size: 0.75rem;
  color: #0071e3;
  font-weight: 600;
  margin-bottom: 2px;
}

.points-value {
  font-size: 1.6rem;
  font-weight: 800;
  color: #0071e3;
}

.points-unit {
  font-size: 0.85rem;
  font-weight: 500;
}

/* 3D Preview Panel */
.preview-panel {
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 22px;
  padding: 24px;
  margin-bottom: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.02);
}

.panel-title {
  text-align: center;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0 0 20px 0;
  letter-spacing: 0.5px;
}

.preview-card-container {
  display: flex;
  justify-content: space-around;
  align-items: center;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 18px;
  padding: 24px;
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.avatar-preview-box-wrapper {
  perspective: 1000px;
}

.avatar-preview-box {
  display: flex;
  align-items: center;
  gap: 16px;
  background: white;
  padding: 14px 24px;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0,0,0,0.02);
  transform-style: preserve-3d;
}

.name-box {
  display: flex;
  flex-direction: column;
}

.username-preview {
  font-size: 0.75rem;
  color: #86868b;
  margin-top: 2px;
}

.message-preview-box {
  width: 45%;
}

.chat-bubble {
  background: white;
  padding: 14px 18px;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
}

.chat-bubble::before {
  content: '';
  position: absolute;
  left: -8px;
  top: 18px;
  border-width: 6px 6px 6px 0;
  border-style: solid;
  border-color: transparent white transparent transparent;
}

.bubble-text {
  font-size: 0.9rem;
  color: #1d1d1f;
  margin: 0;
  line-height: 1.5;
}

/* Skins Grid */
.skins-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
}

.skin-card {
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: 20px;
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.skin-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 28px rgba(0, 113, 227, 0.06);
  border-color: rgba(0, 113, 227, 0.15);
}

.skin-card.previewing {
  border-color: #0071e3;
  box-shadow: 0 12px 28px rgba(0, 113, 227, 0.08);
}

.skin-card.equipped {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(0, 113, 227, 0.02) 100%);
  border-color: rgba(0, 113, 227, 0.25);
}

.status-indicator {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 8px;
  font-weight: 600;
}

.equipped-badge {
  background: #34c759;
  color: white;
}

.preview-badge {
  background: #0071e3;
  color: white;
}

.frame-demo-box {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 70px;
  height: 70px;
}

.skin-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.skin-name {
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 4px 0;
}

.status-tag {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}

.status-tag.unlocked {
  background: rgba(52, 199, 89, 0.1);
  color: #34c759;
}

.status-tag.locked {
  background: rgba(0, 0, 0, 0.04);
  color: #86868b;
}

.skin-card-body {
  flex-grow: 1;
  margin-bottom: 18px;
}

.skin-desc {
  font-size: 0.82rem;
  color: #6e6e73;
  line-height: 1.5;
  margin: 0;
  height: 38px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.skin-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid rgba(0, 0, 0, 0.04);
  padding-top: 12px;
}

.skin-price {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.price-num {
  font-size: 1.3rem;
  font-weight: 800;
  color: #1d1d1f;
}

.price-unit {
  font-size: 0.75rem;
  color: #86868b;
}

.skin-card-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-btn.active {
  background: rgba(0, 113, 227, 0.08);
  color: #0071e3;
  cursor: default;
}

.action-btn.equip {
  background: #0071e3;
  color: white;
}

.action-btn.equip:hover {
  background: #0077ed;
}

.action-btn.buy {
  background: #1d1d1f;
  color: white;
}

.action-btn.buy:hover:not(:disabled) {
  background: #2d2d2f;
}

.action-btn.buy:disabled {
  background: #e3e3e5;
  color: #aeaeaf;
  cursor: not-allowed;
}

/* ========================================== */
/* ======== DARK THEME COMPATIBILITY ======== */
/* ========================================== */

:global(body.dark-theme) {
  .custom-modal-container {
    background: rgba(30, 31, 34, 0.8);
    border-color: rgba(255, 255, 255, 0.06);
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
  }
  .close-btn {
    background: rgba(255, 255, 255, 0.05);
    color: #e4e7ed;
  }
  .close-btn:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  .modal-loading-overlay {
    background: rgba(30, 31, 34, 0.6);
  }
  .shop-header {
    background: rgba(43, 45, 49, 0.4);
    border-color: rgba(255, 255, 255, 0.06);
  }
  .shop-title {
    background: linear-gradient(135deg, #ffffff 0%, #409EFF 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .shop-subtitle {
    color: rgba(255, 255, 255, 0.5);
  }
  .points-badge {
    background: rgba(64, 158, 255, 0.12);
    border-color: rgba(64, 158, 255, 0.2);
  }
  .points-label, .points-value, .points-unit {
    color: #409EFF;
  }
  .preview-panel {
    background: rgba(43, 45, 49, 0.4);
    border-color: rgba(255, 255, 255, 0.06);
  }
  .panel-title {
    color: #ffffff;
  }
  .preview-card-container {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.04);
  }
  .avatar-preview-box {
    background: #1e1f22;
    border-color: rgba(255, 255, 255, 0.04);
  }
  .username-preview {
    color: rgba(255, 255, 255, 0.4);
  }
  .chat-bubble {
    background: #1e1f22;
    border-color: rgba(255, 255, 255, 0.06);
  }
  .chat-bubble::before {
    border-color: transparent #1e1f22 transparent transparent;
  }
  .bubble-text {
    color: #e4e7ed;
  }
  .skin-card {
    background: rgba(43, 45, 49, 0.45);
    border-color: rgba(255, 255, 255, 0.06);
  }
  .skin-card:hover {
    border-color: rgba(64, 158, 255, 0.25);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
  }
  .skin-card.previewing {
    border-color: #409EFF;
  }
  .skin-card.equipped {
    background: linear-gradient(135deg, rgba(43, 45, 49, 0.6) 0%, rgba(64, 158, 255, 0.03) 100%);
    border-color: rgba(64, 158, 255, 0.2);
  }
  .status-tag.locked {
    background: rgba(255, 255, 255, 0.04);
    color: rgba(255, 255, 255, 0.4);
  }
  .skin-desc {
    color: rgba(255, 255, 255, 0.55);
  }
  .skin-card-footer {
    border-top-color: rgba(255, 255, 255, 0.06);
  }
  .price-num {
    color: #ffffff;
  }
  .price-unit {
    color: rgba(255, 255, 255, 0.4);
  }
  .action-btn.buy {
    background: #ffffff;
    color: #1d1d1f;
  }
  .action-btn.buy:hover:not(:disabled) {
    background: #f5f5f7;
  }
  .action-btn.buy:disabled {
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.25);
  }
  .nickname-preview.skin-default {
    color: #ffffff;
  }
  .skin-name.skin-default {
    color: #ffffff;
  }
}

/* Transitons */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .custom-modal-container {
  animation: zoom-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-fade-leave-active .custom-modal-container {
  animation: zoom-out 0.25s cubic-bezier(0.4, 0, 1, 1);
}

@keyframes zoom-in {
  from { transform: scale(0.92); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

@keyframes zoom-out {
  from { transform: scale(1); opacity: 1; }
  to { transform: scale(0.95); opacity: 0; }
}
</style>
